/**
 * Zod input schemas: creditCards. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { ALLOWED_RECEIPT_MIME_TYPES } from '../constants.js';
import { IsoDateStringSchema, ShortTextSchema } from './primitives.js';
// ===========================================================================
// CREDIT CARD SUBMISSIONS
// ===========================================================================
/**
 * Client-side validation for the "New credit card submission" form. The
 * period is picked as a calendar month and rendered to a label like
 * "April 2026"; `periodStart`/`periodEnd` are filled from the PDF during
 * parsing, not entered here.
 */
export const CreditCardStatementCreateSchema = z.object({
    periodLabel: ShortTextSchema,
});
/** Trigger server-side parsing (or retry) of a credit card statement PDF. */
export const CreditCardParseStatementSchema = z.object({
    statementId: z.string().min(1),
});
/**
 * Signed money amount for a card transaction — negative allowed (refunds).
 * Bounded to ±1,000,000 to catch unit/entry errors.
 */
export const SignedMoneyAmountSchema = z
    .number()
    .finite()
    .gte(-1_000_000, 'Amount is implausibly large')
    .lte(1_000_000, 'Amount is implausibly large');
/** Edit a credit card line item (owner, draft only). */
export const CreditCardUpdateLineItemSchema = z
    .object({
    statementId: z.string().min(1),
    lineItemId: z.string().min(1),
    merchant: z.string().trim().min(1, 'Merchant is required').max(200),
    amount: SignedMoneyAmountSchema,
    transactionDate: IsoDateStringSchema,
    /** Layer-1 type. Null = untyped. Validity checked server-side vs config. */
    type: z
        .union([z.string().trim().max(60), z.null(), z.undefined()])
        .transform((v) => (v == null || v === '' ? null : v)),
    /** Layer-2 subtype — validated server-side against the type's config subtypes. */
    subtype: z
        .union([z.string().trim().max(60), z.null(), z.undefined()])
        .transform((v) => (v == null || v === '' ? null : v)),
    /** Grouping-layer value (Platform/Team) — validated server-side vs config. */
    groupValue: z
        .union([z.string().trim().max(120), z.null(), z.undefined()])
        .transform((v) => (v == null || v === '' ? null : v)),
    /** Business purpose — may be empty while editing; submit enforces presence. */
    businessReason: z.string().trim().max(2000).default(''),
    personalCharge: z.boolean(),
    personalChargeReason: z
        .union([z.string().trim().max(2000), z.null()])
        .transform((v) => (v == null || v === '' ? null : v)),
})
    .superRefine((data, ctx) => {
    if (data.personalCharge && !data.personalChargeReason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['personalChargeReason'],
            message: 'A reason is required when flagging a personal charge',
        });
    }
    // type/subtype validity is config-driven, so it's enforced in the callable.
});
/** Submit a credit card statement for approval (owner). */
export const CreditCardSubmitSchema = z.object({
    statementId: z.string().min(1),
    confirmAccurate: z.literal(true, {
        errorMap: () => ({
            message: 'You must confirm the entries are accurate before submitting',
        }),
    }),
    /**
     * Explanation for submitting a non-reconciling statement. Optional here —
     * the server requires it only when the statement doesn't reconcile.
     */
    reconciliationOverrideReason: z
        .union([z.string().trim().max(2000), z.null(), z.undefined()])
        .transform((v) => (v == null || v === '' ? null : v)),
});
/** Approve a submitted statement (approver). */
export const CreditCardFinalReminderSchema = z.object({
    statementId: z.string().min(1),
});
export const CreditCardApproveSchema = z.object({
    statementId: z.string().min(1),
});
/** Reject a submitted statement with a mandatory reason (approver). */
export const CreditCardRejectSchema = z.object({
    statementId: z.string().min(1),
    reason: ShortTextSchema,
});
/** Recall a submitted statement back to draft (owner). */
export const CreditCardRecallSchema = z.object({
    statementId: z.string().min(1),
});
/** Delete a pre-approval statement + its line items and Storage files (owner). */
export const CreditCardDeleteSchema = z.object({
    statementId: z.string().min(1),
});
/** Attach a library receipt to a line item (owner). */
export const CreditCardAttachFromLibrarySchema = z.object({
    statementId: z.string().min(1),
    lineItemId: z.string().min(1),
    libraryItemId: z.string().min(1),
});
/** Assign an admin-uploaded draft to a provisioned user (approver/super-admin). */
export const CreditCardAssignSchema = z.object({
    statementId: z.string().min(1),
    assigneeEmail: z.string().trim().toLowerCase().email(),
});
/** Attach or clear a line item's receipt (owner, draft only). */
export const CreditCardSetReceiptSchema = z.object({
    statementId: z.string().min(1),
    lineItemId: z.string().min(1),
    receipt: z
        .union([
        z.object({
            storagePath: z.string().min(1),
            filename: z.string().min(1).max(255),
            mimeType: z.enum(ALLOWED_RECEIPT_MIME_TYPES),
        }),
        z.null(),
    ]),
});
// ===========================================================================
// RESOURCE
// ===========================================================================
export const CreditCardRequestReceiptSchema = z.object({
    statementId: z.string().min(1),
    lineItemId: z.string().min(1),
    requestedEmail: z.string().trim().toLowerCase().email(),
    // nullish, not optional: the Firebase callable serializer sends an
    // `undefined` property as null, which .optional() rejects.
    note: z.string().trim().max(500).nullish().transform((v) => (v ? v : undefined)),
});
/** Request receipts for SEVERAL line items at once, all from the same person.
 *  Creates one request per line (each independently fulfilled/declined) + one
 *  combined email. */
export const CreditCardRequestReceiptBulkSchema = z.object({
    statementId: z.string().min(1),
    lineItemIds: z.array(z.string().min(1)).min(1).max(100),
    requestedEmail: z.string().trim().toLowerCase().email(),
    note: z.string().trim().max(500).nullish().transform((v) => (v ? v : undefined)),
});
export const CreditCardFulfillReceiptRequestSchema = z.object({
    requestId: z.string().min(1),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    /** Base64 of the receipt file (no data: prefix). Capped server-side. */
    dataBase64: z.string().min(1),
});
/** Upload a receipt into ANOTHER credit-card user's library, on their behalf. */
export const CreditCardLibraryUploadForSchema = z.object({
    targetEmail: z.string().trim().toLowerCase().email(),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    /** Base64 of the receipt file (no data: prefix). Capped server-side. */
    dataBase64: z.string().min(1),
    /** Optional note. Firebase sends an `undefined` value as null, so null (and blank) mean "no note". */
    details: z.string().trim().max(280).nullish().transform((v) => (v ? v : undefined)),
});
export const CreditCardDeclineReceiptRequestSchema = z.object({
    requestId: z.string().min(1),
    comment: z.string().trim().min(1, 'Please add a short comment').max(1000),
});
export const CreditCardCancelReceiptRequestSchema = z.object({
    requestId: z.string().min(1),
});
//# sourceMappingURL=creditCards.js.map